/**
 * Cesium globe view.
 *
 * No Cesium ion (handover 7.3): imagery comes from the Natural Earth II tiles bundled with the
 * CesiumJS distribution and terrain is a plain ellipsoid, so the whole thing runs air-gapped.
 * The tile provider is configurable so LSF can point at self-hosted tiles later.
 */

import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

export interface ImageryConfig {
  /** 'natural-earth' uses the bundled offline tiles. 'url' uses a TMS/XYZ endpoint. */
  kind: 'natural-earth' | 'url';
  url?: string;
  credit?: string;
}

export interface GlobeOptions {
  container: HTMLElement;
  imagery?: ImageryConfig;
  onTick?: (time: Cesium.JulianDate) => void;
}

export class GlobeView {
  readonly viewer: Cesium.Viewer;
  private dataSource: Cesium.CzmlDataSource | null = null;
  private readonly removeTick: Cesium.Event.RemoveCallback;

  private constructor(viewer: Cesium.Viewer, onTick?: (t: Cesium.JulianDate) => void) {
    this.viewer = viewer;
    this.removeTick = viewer.clock.onTick.addEventListener((clock) => {
      onTick?.(clock.currentTime);
    });
  }

  static async create(options: GlobeOptions): Promise<GlobeView> {
    const baseLayer = await createImageryLayer(options.imagery ?? { kind: 'natural-earth' });

    const viewer = new Cesium.Viewer(options.container, {
      baseLayer,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      // Every widget that would reach out to Cesium ion is disabled.
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      navigationHelpButton: false,
      sceneModePicker: false,
      infoBox: false,
      selectionIndicator: false,
      fullscreenButton: false,
      vrButton: false,
      timeline: true,
      animation: true,
      shouldAnimate: false,
      requestRenderMode: false,
    });

    const scene = viewer.scene;
    scene.globe.enableLighting = true; // day/night terminator (handover 7.1.1)
    scene.globe.showGroundAtmosphere = true;
    scene.highDynamicRange = false;
    if (scene.skyAtmosphere) scene.skyAtmosphere.show = true;
    viewer.cesiumWidget.creditContainer.classList.add('cesium-credit-compact');

    return new GlobeView(viewer, options.onTick);
  }

  /**
   * Replace the loaded replay document. Accepts a URL or an already-fetched CZML document;
   * the app fetches the document itself so response-level caveats (`notes` packets) can be
   * surfaced instead of discarded. Returns the spacecraft entities keyed by their id.
   */
  async loadCzml(document: string | object): Promise<Map<string, Cesium.Entity>> {
    if (this.dataSource) {
      this.viewer.dataSources.remove(this.dataSource, true);
      this.dataSource = null;
    }
    const source = await Cesium.CzmlDataSource.load(document);
    await this.viewer.dataSources.add(source);
    this.dataSource = source;

    const entities = new Map<string, Cesium.Entity>();
    for (const entity of source.entities.values) {
      entities.set(entity.id, entity);
    }
    return entities;
  }

  /** Frame the loaded entities without snapping the camera to a single spacecraft. */
  async zoomToAll(): Promise<void> {
    if (!this.dataSource) return;
    await this.viewer.zoomTo(this.dataSource);
  }

  track(entity: Cesium.Entity | undefined): void {
    this.viewer.trackedEntity = entity;
  }

  setModelVisible(entityId: string, visible: boolean): void {
    const entity = this.dataSource?.entities.getById(entityId);
    if (entity?.model) {
      entity.model.show = new Cesium.ConstantProperty(visible);
    }
  }

  setPathVisible(entityId: string, visible: boolean): void {
    const entity = this.dataSource?.entities.getById(entityId);
    if (entity?.path) {
      entity.path.show = new Cesium.ConstantProperty(visible);
    }
  }

  setEclipseVisible(visible: boolean): void {
    if (!this.dataSource) return;
    for (const entity of this.dataSource.entities.values) {
      if (entity.id.startsWith('eclipse/') && entity.polyline) {
        entity.polyline.show = new Cesium.ConstantProperty(visible);
      }
    }
  }

  setGroundTracksVisible(visible: boolean): void {
    if (!this.dataSource) return;
    for (const entity of this.dataSource.entities.values) {
      if (entity.id.startsWith('track/')) {
        entity.show = visible;
      }
    }
  }

  get clock(): Cesium.Clock {
    return this.viewer.clock;
  }

  destroy(): void {
    this.removeTick();
    if (!this.viewer.isDestroyed()) this.viewer.destroy();
  }
}

async function createImageryLayer(config: ImageryConfig): Promise<Cesium.ImageryLayer> {
  if (config.kind === 'url' && config.url) {
    const provider = new Cesium.UrlTemplateImageryProvider({
      url: config.url,
      credit: config.credit ?? '',
    });
    return new Cesium.ImageryLayer(provider);
  }
  // Bundled with CesiumJS itself: no network, no token, no usage policy to respect.
  const provider = await Cesium.TileMapServiceImageryProvider.fromUrl(
    Cesium.buildModuleUrl('Assets/Textures/NaturalEarthII'),
    { credit: 'Natural Earth II (public domain), bundled with CesiumJS' },
  );
  return new Cesium.ImageryLayer(provider);
}
